/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";

interface FormDataType {
  firstName: string;
  lastName: string;
  email: string;
  currency: string;
  amount: string;
  zipCode: string;
  additionalMassage: string;
  agree: boolean;
}

export default function DigitalMortgageCoachForm() {
  const [formData, setFormData] = useState<FormDataType>({
    firstName: "",
    lastName: "",
    email: "",
    currency: "USD",
    amount: "",
    zipCode: "",
    additionalMassage: "",
    agree: false,
  });

  const [step, setStep] = useState<"form" | "verify" | "success">("form");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [agreeError, setAgreeError] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef<HTMLInputElement[]>([]);

  // ✅ Handle Form Changes
  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, type } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  };

  // ✅ Handle OTP Input
  const handleOtpChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return; // Only digits allowed

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move forward
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  // ✅ Handle paste (entire code at once)
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) {
      const digits = pasted.split("");
      setOtp(digits);
      inputRefs.current[5]?.focus();
    }
  };

  // ✅ Submit Form (Step 1)
  const handleSubmitForm = async () => {
    if (!formData.agree) {
      setAgreeError(true);
      return;
    }

    setAgreeError(false);
    setLoading(true);
    setError("");

    const res = await axios.post(
      `${process.env.NEXT_PUBLIC_API_URL}/api/v1/mc/create-mc-soap-user`,
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        currency: formData.currency,
        amount: Number(formData.amount),
        zipCode: formData.zipCode,
        additionalMassage: formData.additionalMassage,
      }
    );

    // 🔥 read backend flag
    const requiresVerification = res?.data?.data?.requiresVerification;

    // ✅ 2FA OFF → skip OTP
    if (requiresVerification === false) {
      setStep("success");
    } else {
      setStep("verify");
    }
  };

  // ✅ Verify OTP (Step 2)
  const handleVerifyOtp = async () => {
    const code = otp.join("").trim();

    if (code.length < 6) {
      toast.error("Please enter the full 6-digit code.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/api/v1/mc/verify`,
        {
          email: formData.email.trim().toLowerCase(),
          otp: code.toString().padStart(6, "0"), // ✅ Keep leading zeros always
        }
      );

      console.log(" Verification success:", res.data);
      setStep("success");
    } catch (err: any) {
      console.error("❌ Verification failed:", err.response?.data || err);
      toast.error(err?.response?.data?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-10">
      {/* ------------------- FORM STEP ------------------- */}
      {step === "form" && (
        <form
          className="flex flex-col gap-5 text-left pt-5"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitForm();
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                First name
              </label>
              <input
                name="firstName"
                type="text"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleFormChange}
                className="border border-gray-300 inter-semibold rounded-md w-full h-11 px-3 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Last name
              </label>
              <input
                name="lastName"
                type="text"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleFormChange}
                className="border border-gray-300 rounded-md inter-semibold w-full h-11 px-3 text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Email</label>
            <input
              name="email"
              type="email"
              placeholder="example@gmail.com"
              value={formData.email}
              onChange={handleFormChange}
              className="border border-gray-300 rounded-md inter-semibold w-full h-11 px-3 text-sm"
              required
            />
          </div>

          {/* --- Currency + Amount --- */}
          <div>
            <label className="block text-sm text-gray-700 mb-2">
              Estimated Purchase Price
            </label>

            <div className="flex flex-col sm:flex-row gap-3">
              <select
                name="currency"
                value={formData.currency}
                onChange={handleFormChange}
                className="border border-gray-300 rounded-lg w-full sm:w-[145px] inter-semibold h-12 pl-4 pr-10 text-sm"
              >
                <option value="USD">$ USD</option>
                <option value="EUR">€ EUR</option>
                <option value="GBP">£ GBP</option>
                <option value="HUF">Ft HUF</option>
              </select>

              <input
                name="amount"
                type="number"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleFormChange}
                className="border border-gray-300 inter-semibold rounded-lg w-full h-12 px-4 text-sm"
                required
              />
            </div>
          </div>

          {/* --- ZIP --- */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">Zip Code</label>
            <input
              name="zipCode"
              type="text"
              placeholder="e.g. 44101"
              value={formData.zipCode}
              onChange={handleFormChange}
              className="border border-gray-300 inter-semibold rounded-md w-full h-11 px-3 text-sm"
              required
            />
          </div>

          {/* --- Additional Message --- */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Additional Message
            </label>
            <textarea
              name="additionalMassage"
              placeholder="Leave a message..."
              value={formData.additionalMassage}
              onChange={handleFormChange}
              className="border border-gray-300 inter-semibold rounded-md w-full h-20 px-3 py-2 text-sm"
            />
          </div>

          {/* --- Agree Checkbox --- */}
          <label className="flex flex-col gap-1 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <input
                name="agree"
                type="checkbox"
                checked={formData.agree}
                onChange={(e) => {
                  handleFormChange(e);
                  if (agreeError) setAgreeError(false);
                }}
                className={`w-4 h-4 border-gray-400 ${
                  agreeError ? " outline-red-500 outline-2" : ""
                }`}
              />
              <span>
                You agree to our{" "}
                <a href="#" className="text-blue-600 underline">
                  privacy policy
                </a>
                .
              </span>
            </div>
            {agreeError && (
              <span className="text-red-500 text-xs mt-1">
                You must agree to proceed.
              </span>
            )}
          </label>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <motion.button
            whileHover={!loading ? { scale: 1.03 } : {}}
            className="w-full flex items-center justify-center gap-2 bg-linear-to-r from-[#4177BC] to-[#5EA8E8] text-white py-3 rounded-md text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            type="submit"
            disabled={loading}
          >
            {loading ? "Processing..." : "Get your Digital Mortgage Coach"}
          </motion.button>
        </form>
      )}

      {/* ------------------- OTP VERIFY STEP ------------------- */}
      {step === "verify" && (
        <div className="text-center space-y-6 bg-white shadow-md rounded-2xl py-5 px-10">
          <div className="text-center mb-8">
            <CheckCircle2 className="mx-auto text-[#4177BC] w-12 h-12 mb-2" />
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              Verification Sent!
            </h2>
            <p className="text-gray-600 text-sm">
              We’ve sent a 6-digit code to <strong>{formData.email}</strong>.
              Enter it below to verify your email.
            </p>

            <p className="text-xs text-gray-400 mt-2">
              Please check your spam or junk folder if you do not receive the
              email.
            </p>
          </div>

          <div className="flex justify-center gap-3">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el: any) => (inputRefs.current[i] = el!)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(e.target.value, i)}
                onKeyDown={(e) => handleOtpKeyDown(e, i)}
                onPaste={handleOtpPaste}
                className="w-12 h-12 text-center border border-gray-400 rounded-md text-lg font-semibold focus:ring-2 focus:ring-[#4177BC] outline-none"
              />
            ))}
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <motion.button
            whileHover={!loading ? { scale: 1.03 } : {}}
            className="w-full sm:w-auto px-8 py-3  bg-linear-to-r from-[#4177BC] to-[#5EA8E8] text-white rounded-md transition-all"
            onClick={handleVerifyOtp}
            disabled={loading}
          >
            {loading ? "Verifying..." : "Verify"}
          </motion.button>
        </div>
      )}

      {/* ------------------- SUCCESS STEP ------------------- */}
      {step === "success" && (
        <div className="text-center py-10 bg-white rounded-2xl shadow-lg border border-[#4177BC]/40">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#4177BC]/10 flex items-center justify-center mb-5">
            <svg
              className="w-9 h-9 text-[#4177BC]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-[#4177BC] mb-3">
            Thank you, {formData.firstName}!
          </h2>

          <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
            Your Mortgage Coach request has been submitted successfully.
          </p>
        </div>
      )}
    </div>
  );
}
